'use client';

import { useEffect, useState } from 'react';
import { isInWishlist, toggleWishlist, WISHLIST_CHANGED, type StoredProduct } from '@/lib/wishlist';
import { trackAddToWishlist, parsePrice } from '@/lib/analytics';

export function WishlistButton({
  product,
  variant = 'inline',
}: {
  product: StoredProduct;
  variant?: 'inline' | 'card';
}) {
  const [active, setActive] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setActive(isInWishlist(product.id));
    setHydrated(true);
    function onChange() { setActive(isInWishlist(product.id)); }
    window.addEventListener(WISHLIST_CHANGED, onChange);
    return () => window.removeEventListener(WISHLIST_CHANGED, onChange);
  }, [product.id]);

  function handleClick(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    const nowIn = toggleWishlist(product);
    setActive(nowIn);
    if (nowIn) {
      trackAddToWishlist({
        item_id:   product.id,
        item_name: product.name,
        price:     parsePrice(product.price),
        quantity:  1,
      });
    }
  }

  if (variant === 'card') {
    return (
      <button
        onClick={handleClick}
        aria-label={active ? 'Remove from favorites' : 'Save to favorites'}
        aria-pressed={active}
        className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-bone/85 backdrop-blur flex items-center justify-center hover:bg-bone transition-colors"
      >
        <Heart filled={hydrated && active} />
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      aria-label={active ? 'Remove from favorites' : 'Save to favorites'}
      aria-pressed={active}
      className="w-full border border-ink/30 text-ink py-4 text-xs uppercase tracking-[0.12em] hover:border-ink transition-colors flex items-center justify-center gap-2"
    >
      <Heart filled={hydrated && active} small />
      <span>{hydrated && active ? 'Saved' : 'Save to favorites'}</span>
    </button>
  );
}

function Heart({ filled, small = false }: { filled: boolean; small?: boolean }) {
  const size = small ? 14 : 18;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={1.5}
      className={filled ? 'text-rose' : ''}
    >
      <path d="M12 21s-7-4.5-9.5-9C1.2 9.4 2.4 6.5 5 5.4c2-.8 4.2 0 5.5 1.6l.5.6.5-.6c1.3-1.6 3.5-2.4 5.5-1.6 2.6 1.1 3.8 4 2.5 6.6C19 16.5 12 21 12 21z" />
    </svg>
  );
}
